import { motion } from "motion/react";
import styled from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import {
  blueGreyMui,
  blueMui,
  greyMui,
  MOBILE_BREAKPOINT,
  rydeColors,
  ScreenDepth,
} from "@ryde/theme";

export const REPORT_CHART_BAR_BORDER_RADIUS = 10;

export const ReportChartBarContainer = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "report-chart-bar-container",
})`
  height: 100%;
  width: 100%;
  min-height: 200px;
`;

export const ReportChartBarScrollArea = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "report-chart-bar-scroll",
})`
  height: 100%;
  width: 100%;
  min-height: 200px;
  overflow-y: auto;
  overflow-x: hidden;
`;

export const ReportChartBarCanvas = styled.div<{ $minHeight?: number }>`
  height: ${({ $minHeight }) => ($minHeight ? `${$minHeight}px` : "100%")};
  width: 100%;
  min-height: ${({ $minHeight }) => ($minHeight ? `${$minHeight}px` : "200px")};
`;

export const ReportChartDrawerOuter = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "report-chart-drawer-outer",
})`
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
`;

export const ReportChartDrawerResizeHandle = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "report-chart-drawer-resize-handle",
  },
)`
  flex-shrink: 0;
  width: 10px;
  background-color: ${greyMui._300};
  cursor: col-resize;
  user-select: none;
  z-index: ${ScreenDepth.GRID_SPLITTER};

  &:hover {
    background-color: ${greyMui._400};
  }

  &:focus-visible {
    outline: 2px solid ${blueMui._500};
    outline-offset: -2px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: none;
  }
`;

export const ReportChartDrawerWrapper = styled(
  motion.div,
).attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "report-chart-drawer-wrapper",
})`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  height: 100%;
  background-color: ${rydeColors.white};

  .ReportChartDrawer-Header {
    flex-shrink: 0;
  }

  .ReportChartDrawer-ToolbarWrap {
    flex-shrink: 0;
    padding: 12px 20px 16px;
    border-bottom: 1px solid ${greyMui._300};
  }

  .ReportChartDrawer-ChartSection {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    padding: 16px 20px 20px;
    overflow: hidden;
  }

  .ReportChartDrawer-ChartBox {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    border: 1px solid ${greyMui._300};
    border-radius: 4px;
    padding: 12px 12px 8px;
    overflow: hidden;
    background-color: ${rydeColors.white};
  }

  .ReportChartDrawer-ChartTitle {
    flex-shrink: 0;
    font-size: 14px;
    font-weight: 700;
    color: ${blueGreyMui._800};
    margin-bottom: 8px;
  }

  .ReportChartDrawer-ChartInner {
    flex: 1;
    min-height: 0;
    width: 100%;
    position: relative;
  }
`;

export const ReportChartToolbarPanel = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "report-chart-toolbar",
})`
  padding: 12px 14px;
  border-radius: 10px;
  background-color: ${greyMui._100};

  .ReportChartToolbar-Body {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .ReportChartToolbar-Row {
    display: flex;
    flex-direction: row;
    gap: 12px;
    align-items: flex-end;
  }

  .ReportChartToolbar-RowControls {
    flex-wrap: wrap;
  }

  .ReportChartToolbar-Field {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;

    .toolbar-caption {
      font-size: 12px;
      font-weight: 500;
      color: ${blueGreyMui._600};
      line-height: 1.2;
    }
  }

  .ReportChartToolbar-AxisField {
    flex: 1 1 0;
  }

  .ReportChartToolbar-DisplayMode {
    flex: 0 0 168px;
    max-width: 168px;
  }

  .ReportChartToolbar-MaxItems {
    flex: 0 0 110px;
    max-width: 110px;
  }

  .ReportChartToolbar-CheckboxesField {
    flex-shrink: 0;
  }

  .ReportChartToolbar-CheckboxesSpacer {
    visibility: hidden;
    user-select: none;
  }

  .ReportChartToolbar-Checkboxes {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    min-height: 40px;
    gap: 4px 16px;
  }

  .ReportChartToolbar-CheckboxLabel {
    margin: 0;
    white-space: nowrap;
    gap: 1px;

    .MuiCheckbox-root {
      padding-block: 6px;
      padding-inline: 6px 1px;
    }

    .MuiFormControlLabel-label {
      margin-inline-start: 0;
      padding-inline-start: 0;
      font-size: 13px;
      color: ${blueGreyMui._700};
    }

    &.Mui-disabled .MuiFormControlLabel-label {
      color: ${blueGreyMui._400};
    }
  }

  .ReportChartToolbar-Actions {
    flex-shrink: 0;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
    margin-inline-start: auto;
    padding-bottom: 2px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    .ReportChartToolbar-Row {
      flex-direction: column;
      align-items: stretch;
    }

    .ReportChartToolbar-DisplayMode,
    .ReportChartToolbar-MaxItems,
    .ReportChartToolbar-CheckboxesField {
      flex: 1 1 auto;
      max-width: none;
    }

    .ReportChartToolbar-Actions {
      margin-inline-start: 0;
      justify-content: end;
    }
  }
`;
